import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { adminSettingsService } from "../api/service/admin/admin_settings.service";
import { AdminSettings } from "../api/interface/admin_settings.interface";

import { useAuth } from "./AuthContext";
import { hasAnyRole } from "../utils/role";

import { servicesService } from "../api/service/admin/admin_services.service";
import { Service } from "../api/interface/services.interface";

import { adminProductCategoriesService } from "../api/service/admin/admin_productcategories.service";
import { ProductCategory } from "../api/interface/productcategories.interface";

import { adminProductsService } from "../api/service/admin/admin_products.service";
import { Product } from "../api/interface/products.interface";

// ===============================
// IMPORT DES SERVICES ARTICLES & CATEGORIES D'ARTICLES
// ===============================
import { adminArticleCategoriesService } from "../api/service/admin/admin_articlescategories.service";
import { adminArticlesService } from "../api/service/admin/admin_articles.service";
import { ArticleCategory } from "../api/interface/articlescategories.interface";
import { Article } from "../api/interface/articles.interface";


import { adminUsersService } from "../api/service/admin/admin_users.service";
import { adminRolesService } from "../api/service/admin/admin_roles.service";
import { User } from "../api/interface/user.interface";
import { Role } from "../api/interface/roles.interface";


// ===============================
// TYPES
// ===============================
interface AdminContextType {
  settings: AdminSettings | null;
  services: Service[] | null;
  productCategories: ProductCategory[] | null;
  products: Product[] | null;

  articleCategories: ArticleCategory[] | null;  // 🔥 NOUVEAU
  articles: Article[] | null;                   // 🔥 NOUVEAU

  isLoading: boolean;
  isServicesLoading: boolean;
  isProductCategoriesLoading: boolean;
  isProductsLoading: boolean;

  isArticleCategoriesLoading: boolean;  // 🔥 NOUVEAU
  isArticlesLoading: boolean;           // 🔥 NOUVEAU

  refreshSettings: () => Promise<void>;
  refreshServices: () => Promise<void>;
  refreshProductCategories: () => Promise<void>;
  refreshProducts: () => Promise<void>;

  refreshArticleCategories: () => Promise<void>;  // 🔥 NOUVEAU
  refreshArticles: () => Promise<void>;           // 🔥 NOUVEAU
  // ...................
  users: User[] | null;
  roles: Role[] | null;
  isUsersLoading: boolean;
  isRolesLoading: boolean;
  refreshUsers: () => Promise<void>;
  refreshRoles: () => Promise<void>;


}

// ===============================
// CONTEXT
// ===============================
const AdminContext = createContext<AdminContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  // ===============================
  // SETTINGS
  // ===============================
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isFetchingSettingsRef = useRef(false);

  // ===============================
  // SERVICES
  // ===============================
  const [services, setServices] = useState<Service[] | null>(null);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const isServicesFetchingRef = useRef(false);

  // ===============================
  // PRODUCT CATEGORIES
  // ===============================
  const [productCategories, setProductCategories] = useState<ProductCategory[] | null>(null);
  const [isProductCategoriesLoading, setIsProductCategoriesLoading] = useState(false);
  const isProductCategoriesFetchingRef = useRef(false);

  // ===============================
  // PRODUCTS
  // ===============================
  const [products, setProducts] = useState<Product[] | null>(null);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const isProductsFetchingRef = useRef(false);

  // ===============================
  // ARTICLE CATEGORIES 🔥
  // ===============================
  const [articleCategories, setArticleCategories] = useState<ArticleCategory[] | null>(null);
  const [isArticleCategoriesLoading, setIsArticleCategoriesLoading] = useState(false);
  const isArticleCategoriesFetchingRef = useRef(false);

  // ===============================
  // ARTICLES 🔥
  // ===============================
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [isArticlesLoading, setIsArticlesLoading] = useState(false);
  const isArticlesFetchingRef = useRef(false);
  // ===============================
  // Roles and  Users 🔥
  // ===============================
  const [users, setUsers] = useState<User[] | null>(null);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const isUsersFetchingRef = useRef(false);
  const isRolesFetchingRef = useRef(false);

  // ===============================
  // LOAD METHODS
  // ===============================
  const loadSettings = useCallback(async () => {
    if (isFetchingSettingsRef.current) return;
    isFetchingSettingsRef.current = true;
    try {
      if (!hasAnyRole(user, ["admin", "platform_admin"])) {
        setSettings(null);
        return;
      }
      setIsLoading(true);
      const data = await adminSettingsService.getSettings();
      setSettings(data);
    } catch {
      setSettings(null);
    } finally {
      isFetchingSettingsRef.current = false;
      setIsLoading(false);
    }
  }, [user]);

  const loadServices = useCallback(async () => {
    if (isServicesFetchingRef.current) return;
    isServicesFetchingRef.current = true;
    try {
      setIsServicesLoading(true);
      const res = await servicesService.getServices();
      setServices(res.data.services);
    } catch {
      setServices(null);
    } finally {
      isServicesFetchingRef.current = false;
      setIsServicesLoading(false);
    }
  }, []);

  const loadProductCategories = useCallback(async () => {
    if (isProductCategoriesFetchingRef.current) return;
    isProductCategoriesFetchingRef.current = true;
    try {
      setIsProductCategoriesLoading(true);
      const res = await adminProductCategoriesService.getProductCategories();
      setProductCategories(res.data.categories);
    } catch {
      setProductCategories(null);
    } finally {
      isProductCategoriesFetchingRef.current = false;
      setIsProductCategoriesLoading(false);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    if (isProductsFetchingRef.current) return;
    isProductsFetchingRef.current = true;
    try {
      setIsProductsLoading(true);
      const res = await adminProductsService.getProducts();
      setProducts(res.data.products);
    } catch {
      setProducts(null);
    } finally {
      isProductsFetchingRef.current = false;
      setIsProductsLoading(false);
    }
  }, []);

  const loadArticleCategories = useCallback(async () => {
    if (isArticleCategoriesFetchingRef.current) return;
    isArticleCategoriesFetchingRef.current = true;
    try {
      setIsArticleCategoriesLoading(true);
      const res = await adminArticleCategoriesService.getArticleCategories();
      setArticleCategories(res.data.categories);
    } catch {
      setArticleCategories(null);
    } finally {
      isArticleCategoriesFetchingRef.current = false;
      setIsArticleCategoriesLoading(false);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    if (isArticlesFetchingRef.current) return;
    isArticlesFetchingRef.current = true;
    try {
      setIsArticlesLoading(true);
      const res = await adminArticlesService.getArticles();
      setArticles(res.data.articles);
    } catch {
      setArticles(null);
    } finally {
      isArticlesFetchingRef.current = false;
      setIsArticlesLoading(false);
    }
  }, []);



  // Fonctions loadUsers, loadRoles
  const loadUsers = useCallback(async () => {
    if (isUsersFetchingRef.current) return;
    isUsersFetchingRef.current = true;
    try {
      setIsUsersLoading(true);
      const res = await adminUsersService.getUsers();
      setUsers(res.data.users);
    } catch (error) {
      console.error(error);
      setUsers(null);
    } finally {
      isUsersFetchingRef.current = false;
      setIsUsersLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    if (isRolesFetchingRef.current) return;
    isRolesFetchingRef.current = true;
    try {
      setIsRolesLoading(true);
      const res = await adminRolesService.getRoles();
      setRoles(res.data.roles);
    } catch (error) {
      console.error(error);
      setRoles(null);
    } finally {
      isRolesFetchingRef.current = false;
      setIsRolesLoading(false);
    }
  }, []);


  // ===============================
  // REFRESH METHODS
  // ===============================
  const refreshSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  const refreshServices = useCallback(async () => {
    await loadServices();
  }, [loadServices]);

  const refreshProductCategories = useCallback(async () => {
    await loadProductCategories();
  }, [loadProductCategories]);

  const refreshProducts = useCallback(async () => {
    await loadProducts();
  }, [loadProducts]);

  const refreshArticleCategories = useCallback(async () => {
    await loadArticleCategories();
  }, [loadArticleCategories]);

  const refreshArticles = useCallback(async () => {
    await loadArticles();
  }, [loadArticles]);

  // refresh methods
  const refreshUsers = useCallback(async () => { await loadUsers(); }, [loadUsers]);
  const refreshRoles = useCallback(async () => { await loadRoles(); }, [loadRoles]);


  // ===============================
  // INIT
  // ===============================
  useEffect(() => {
    loadSettings();
    loadServices();
    loadProductCategories();
    loadProducts();
    loadArticleCategories();  // 🔥
    loadArticles();           // 🔥
    loadUsers();
    loadRoles();
  }, [
    loadSettings,
    loadServices,
    loadProductCategories,
    loadProducts,
    loadArticleCategories,
    loadArticles,
    loadUsers,
    loadRoles,
  ]);

  // ===============================
  // VALUE
  // ===============================
  const value = useMemo(
    () => ({
      settings,
      services,
      productCategories,
      products,
      articleCategories,
      articles,
      isLoading,
      isServicesLoading,
      isProductCategoriesLoading,
      isProductsLoading,
      isArticleCategoriesLoading,
      isArticlesLoading,
      refreshSettings,
      refreshServices,
      refreshProductCategories,
      refreshProducts,
      refreshArticleCategories,
      refreshArticles,
      users,
      roles,
      isUsersLoading,
      isRolesLoading,
      refreshUsers,
      refreshRoles,

    }),
    [
      settings,
      services,
      productCategories,
      products,
      articleCategories,
      articles,
      isLoading,
      isServicesLoading,
      isProductCategoriesLoading,
      isProductsLoading,
      isArticleCategoriesLoading,
      isArticlesLoading,
      refreshSettings,
      refreshServices,
      refreshProductCategories,
      refreshProducts,
      refreshArticleCategories,
      refreshArticles,
      users,
      roles,
      isUsersLoading,
      isRolesLoading,
      refreshUsers,
      refreshRoles,

    ]
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// ===============================
// HOOK
// ===============================
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin doit être utilisé dans AdminProvider");
  }
  return context;
};